"use client";

import Image from "next/image";
import { useRef, useEffect, useState } from "react";
import { useInView } from "@/hooks/useInView";
import { motion } from "framer-motion";
import EnrollTourButtons from "@/components/cms/EnrollTourButtons";
import { useParallaxOffset } from "@/hooks/useParallaxOffset";



export default function BoardingSchoolHero({ cms, logoUrl }: { cms?: Record<string, string>; logoUrl?: string | null }) {
  const headingRef = useRef<HTMLDivElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  // Use optimized parallax hook
  const [offset, isDesktop, transform3d] = useParallaxOffset(0.2, 640, parallaxRef);
  const showHeading = useInView(headingRef, {
    margin: "0px",
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center font-nunito overflow-hidden">
      {/* Parallax Background */}
      <div
        ref={parallaxRef}
        className="absolute will-change-transform w-full -top-[10%] h-[120%]"
        style={mounted && isDesktop ? { transform: transform3d } : {}}
      >
        {cms?.bg_image_url && <Image
          src={cms.bg_image_url}
          alt="Boarding School Hero Background"
          fill
          className="object-cover"
          priority={false}
          loading="eager"
          sizes="100vw"
          placeholder="blur"
          blurDataURL="data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA="
        />}
      </div>
      {/* Overlay — outside parallax container so it is never inside a GPU compositor layer */}
      <div
        className="absolute inset-0"
        style={{ background: cms?.overlay_color ?? "rgba(6,22,82,0.6)" }}
      ></div>
      {/* Content */}
      <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto flex flex-col items-center pt-12 pb-12 sm:pt-24 sm:pb-24">
        {logoUrl && (
        <div className="mb-8 pt-8">
          <Image
            src={logoUrl}
            alt="Ashton John's Private School Logo"
            width={200}
            height={200}
            className="mx-auto w-52 h-48"
            priority
          />
        </div>
        )}
        <motion.div
          ref={headingRef}
          initial={{ opacity: 0, y: 60 }}
          animate={showHeading ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
          transition={{ type: "spring", stiffness: 120, damping: 20, duration: 0.6 }}
          className="space-y-4 mb-12"
        >
          <p className="text-lg sm:text-xl md:text-2xl font-light leading-relaxed">
            {cms?.pre_heading /* || "Discover your home away from home at our" */}
          </p>
          <h1 className="text-4xl md:text-6xl font-bold mb-2">
            {cms?.heading /* || "4 STAR BOARDING SCHOOL" */}
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl font-light leading-relaxed">
            {cms?.post_heading /* || "Ashton John's Boarding School - Established 2025" */}
          </p>
          <h2 className="text-2xl md:text-3xl font-bold mb-6">
            {cms?.application_text /* || "APPLICATION NOW OPEN!" */}
          </h2>
        </motion.div>
        <EnrollTourButtons
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
          buttonClassName="bg-school-gold hover:bg-school-gold-dark text-black font-bold py-4 px-8 rounded text-lg transition-colors text-center"
          enrollText={cms?.enrol_btn_text}
          tourText={cms?.tour_btn_text}
          enrollUrl={cms?.enrol_btn_url /* ?? "/contact-us?option=enrol&boarding=true#contact-form" */}
          tourUrl={cms?.tour_btn_url /* ?? "/contact-us?option=tour&boarding=true#contact-form" */}
        />
      </div>
    </section>
  );
}
